/**
 * Laptop (MacBook) hero mockup — screen with content + cinematic float.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  typeSize, weights, withAlpha, colors as themeColors, gsapSetLine,
} from "../../reel/sceneShared.js";
import { laptopFrame, screenContent } from "./_mockHelpers.js";

export const laptopMockup: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const headline = inputs.text ?? "";
  const caption = inputs.subtext ?? "";
  const isVertical = ctx.format === "9:16";

  const laptopW = isVertical ? 920 : ctx.format === "1:1" ? 880 : 1180;
  const lg = typeSize("lg", ctx.format);
  const sm = typeSize("sm", ctx.format);

  const wrapId = `${clipPrefix}-wrap`;
  const headId = `${clipPrefix}-head`;
  const capId = `${clipPrefix}-cap`;
  const deviceId = `${clipPrefix}-device`;
  const glowId = `${clipPrefix}-glow`;
  const veilId = `${clipPrefix}-veil`;

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "studio", accent });

  const inner = screenContent({ media: inputs.media, accent, fallback: "feed", objectFit: "contain" });

  // Dark exit veil: deckt am Ende den hellen Laptop-Screen + Akzent-Glow
  // ab, damit die Szene visuell ins Schwarze fadet (nicht nur der Wrapper
  // transparent wird). Verhindert den „hellen Flash" Richtung Sign-Off.
  const veilHtml = `<div id="${veilId}" style="position:absolute;inset:0;background:${themeColors.bg};opacity:0;pointer-events:none;z-index:9;"></div>`;

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:40px;padding:80px;perspective:1800px;">
    ${headline ? `<div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${lg * 0.78}px;letter-spacing:-2px;color:${themeColors.fg};line-height:1.05;text-align:center;text-wrap:balance;max-width:${isVertical ? 880 : 1200}px;">${escapeHtml(headline)}</div>` : ""}
    <div style="position:relative;display:flex;justify-content:center;">
      <div id="${glowId}" style="position:absolute;inset:-40px;border-radius:120px;background:radial-gradient(closest-side, ${withAlpha(accent, 0.45)} 0%, transparent 70%);filter:blur(28px);"></div>
      ${laptopFrame({ id: deviceId, width: laptopW, accent, inner })}
    </div>
    ${caption ? `<div id="${capId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${sm * 0.85}px;letter-spacing:6px;text-transform:uppercase;color:${withAlpha(themeColors.fg, 0.7)};">${escapeHtml(caption)}</div>` : ""}
    ${veilHtml}
  </div>`;

  const init = [
    headline ? gsapSetLine(`#${headId}`, { opacity: 0, y: 18 }) : "",
    gsapSetLine(`#${deviceId}`, { opacity: 0, y: 60, rotationX: 18, scale: 0.92, transformOrigin: "50% 70%" }),
    gsapSetLine(`#${glowId}`, { opacity: 0 }),
    caption ? gsapSetLine(`#${capId}`, { opacity: 0, y: 12 }) : "",
    gsapSetLine(`#${veilId}`, { opacity: 0 }),
  ].filter(Boolean).join("\n");

  // Veil-Einblendung beginnt 0.8 s vor Scene-Ende und ist bei -0.2 s voll
  // deckend, danach übernimmt der reguläre clipOutroFade (Wrap → 0) während
  // der Screen bereits schwarz ist.
  const veilDur = Math.min(0.8, Math.max(0.4, clipDurationSec * 0.18));
  const veilStart = Math.max(0, clipDurationSec - veilDur - 0.2);

  const tweens: string[] = [
    t.to(`#${deviceId}`, { opacity: 1, y: 0, rotationX: 0, scale: 1, duration: 1.1, ease: "raw:\"power3.out\"" }, 0.05),
    t.to(`#${glowId}`, { opacity: 0.9, duration: 0.8, ease: "raw:\"power2.out\"" }, 0.2),
    t.to(`#${glowId}`, { opacity: 0.5, duration: Math.max(0.8, clipDurationSec - 1.6), ease: "raw:\"sine.inOut\"" }, 1.0),
    t.to(`#${deviceId}`, { y: -8, duration: Math.max(1.0, clipDurationSec - 1.8), ease: "raw:\"sine.inOut\"" }, 1.0),
    t.to(`#${veilId}`, { opacity: 1, duration: veilDur, ease: "raw:\"power2.in\"" }, veilStart),
  ];
  if (headline) tweens.push(t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power2.out\"" }, 0.35));
  if (caption) tweens.push(t.to(`#${capId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 0.7));
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 14));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
